import type { TenantPresentation } from "@/hooks/use-tenant-presentation";

const THEME_COLORS: Record<string, string> = {
  murtada: "#276749",
  mufid: "#174e75",
};

const STATIC_FAVICONS: Record<string, string> = {
  murtada: "/brand/murtada-logo.png",
  mufid: "/brand/mufid-logo.png",
};

export interface TenantMetadataValues {
  title: string;
  description: string;
  themeColor: string;
  faviconUrl: string;
  openGraphImageUrl: string | null;
}

export function metadataForTenant(
  presentation: Pick<TenantPresentation, "name" | "logoUrl" | "contentSlug" | "content">,
): TenantMetadataValues {
  return {
    title: `${presentation.name} Portal`,
    description: presentation.content.footer.description,
    themeColor: THEME_COLORS[presentation.contentSlug] ?? "#334155",
    faviconUrl:
      presentation.logoUrl ?? STATIC_FAVICONS[presentation.contentSlug] ?? "/favicon.svg",
    openGraphImageUrl: presentation.logoUrl,
  };
}

function setMeta(
  document: Document,
  attribute: "name" | "property",
  key: string,
  content: string,
) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.append(element);
  }
  element.content = content;
}

export function updateDocumentMetadata(
  metadata: TenantMetadataValues,
  document: Document = window.document,
) {
  document.title = metadata.title;
  setMeta(document, "name", "description", metadata.description);
  setMeta(document, "name", "theme-color", metadata.themeColor);
  setMeta(document, "property", "og:title", metadata.title.replace(/ Portal$/, ""));
  setMeta(document, "property", "og:description", metadata.description);

  let icon = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!icon) {
    icon = document.createElement("link");
    icon.rel = "icon";
    document.head.append(icon);
  }
  icon.href = metadata.faviconUrl;

  const openGraphImage = document.head.querySelector<HTMLMetaElement>('meta[property="og:image"]');
  if (metadata.openGraphImageUrl) {
    setMeta(document, "property", "og:image", metadata.openGraphImageUrl);
  } else {
    openGraphImage?.remove();
  }
}
